/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Alert, Button } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IQuickOrderGridState } from './utilities';

/**
 * Interface quick order grid form props.
 */
export interface IQuickOrderGridFormProps {
    isAddtoListDisabled: boolean | undefined;
    productTextInput: React.RefObject<HTMLInputElement>;
    quantityTextInput: React.RefObject<HTMLInputElement>;
    maxChars: number;
    minQuantity?: number;
    maxQuantity?: number;
    moduleClassName: string;
    ariaLabelQuantity: string;
    ariaLabelProduct: string;
    ariaLabelAddtoCart: string;
    quickOrderGridState: IQuickOrderGridState;
    handleInputChange(query: string): void;
    handleQuantityChange(query: string): void;
    handleAddProductListClick(): Promise<void>;
}

/**
 * Interface quick order grid form view props.
 */
export interface IQuickOrderGridFormViewProps {
    product: React.ReactNode;
    quantity: React.ReactNode;
    addToListBtn: React.ReactNode;
    userMessage?: React.ReactNode;
}

/**
 * On Input Change functionality.
 * @param handleInputChange -Input change function.
 * @returns Input change value.
 */
const onInputChangeFunction = (handleInputChange: (query: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(event.target.value || '');
};

/**
 * On Quantity Change functionality.
 * @param handleQuantityChange -Quantity change function.
 * @returns Quantity change value.
 */
const onQuantityChangeFunction = (handleQuantityChange: (query: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleQuantityChange(event.target.value || '');
};

/**
 * Quick order grid form component.
 * @param props -Component form props.
 * @returns Quick order gridp view props..
 */
export const QuickOrderGridFormComponent = (props: IQuickOrderGridFormProps): IQuickOrderGridFormViewProps => {
    const {
        moduleClassName,
        productTextInput,
        quantityTextInput,
        quickOrderGridState,
        maxChars,
        minQuantity,
        maxQuantity,
        isAddtoListDisabled,
        ariaLabelQuantity,
        ariaLabelProduct,
        ariaLabelAddtoCart,
        handleQuantityChange,
        handleInputChange,
        handleAddProductListClick
    } = props;

    const onInputChange = onInputChangeFunction(handleInputChange);

    const onQuantityInputChange = onQuantityChangeFunction(handleQuantityChange);

    const productNode = (
        <div className={`${moduleClassName}__content-form-main-product`}>
            <h2 className={`${moduleClassName}__content-form-main-product-lable`} aria-label={ariaLabelProduct}>
                {ariaLabelProduct}
            </h2>
            <input
                className={`${moduleClassName}__content-form-main-product-input`}
                type='text'
                aria-label={ariaLabelProduct}
                ref={productTextInput}
                onChange={onInputChange}
            />
        </div>
    );

    const quantityNode = (
        <div className={`${moduleClassName}__content-form-main-quantity`}>
            <h2 className={`${moduleClassName}__content-form-main-quantity-lable`} aria-label={ariaLabelQuantity}>
                {ariaLabelQuantity}
            </h2>
            <input
                className={`${moduleClassName}__content-form-main-quantity-input`}
                pattern='[0-9]*'
                ref={quantityTextInput}
                onChange={onQuantityInputChange}
                maxLength={maxChars}
                type='number'
                aria-live='polite'
                aria-label={ariaLabelQuantity}
                aria-valuemin={minQuantity}
                aria-valuemax={maxQuantity}
                aria-valuenow={quickOrderGridState.quantity}
            />
        </div>
    );

    const addToListNode = (
        <div className={`${moduleClassName}__content-form-main-add-to-product-list`}>
            <Button
                className={`${moduleClassName}__content-form-main-add-to-product-list-button`}
                disabled={isAddtoListDisabled}
                aria-label={ariaLabelAddtoCart}
                onClick={handleAddProductListClick}
            >
                {ariaLabelAddtoCart}
            </Button>
        </div>
    );

    const userMessageNode = quickOrderGridState.stateMessage ? (
        <Alert color={quickOrderGridState.stateMessage.statusCssString} isOpen={quickOrderGridState.stateMessage.isOpen}>
            <span className={quickOrderGridState.stateMessage.messageGlyph} />
            <span>{quickOrderGridState.stateMessage.messageText}</span>
        </Alert>
    ) : (
        undefined
    );

    return {
        product: productNode,
        addToListBtn: addToListNode,
        quantity: quantityNode,
        userMessage: userMessageNode
    };
};
